import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import { GameButtonSize } from '../gameButton';

import { CSSGradientToSVG, getDefaultGradient } from './utils';

import styles from './BorderGradient.module.css';

export interface BorderGradientProps {
  gradient: boolean | string;
  size?: GameButtonSize;
  label?: ReactNode;
}

interface SizeStateProps {
  width: number;
  height: number;
}

const BorderGradient: FC<BorderGradientProps> = ({ gradient, size, label }) => {
  const gradientRef = useRef<HTMLDivElement>(null);
  const [sizeState, setSizeState] = useState<SizeStateProps | null>(null);

  const gradientID = 'buttonBorderGradient_' + useId();

  const handleSize = useCallback(() => {
    if (gradientRef.current) {
      const { height, width } = gradientRef.current.getBoundingClientRect();

      setSizeState({ width: Math.floor(width * 10), height: Math.floor(height * 10) });
    }
  }, []);

  const gradientElement = useMemo(() => {
    return typeof gradient === 'boolean' ? getDefaultGradient(gradientID) : CSSGradientToSVG(gradient, gradientID);
  }, [gradient, gradientID]);

  useEffect(() => {
    if (gradientRef.current) {
      handleSize();

      if (!size) {
        window.addEventListener('resize', handleSize, { passive: true });

        return (): void => {
          window.removeEventListener('resize', handleSize);
        };
      }
    }
    // do not delete label from the dependencies
  }, [handleSize, size, label]);

  return (
    <span className={styles.borderWrapper} ref={gradientRef}>
      {sizeState?.height && sizeState?.width ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={styles.svg}
          viewBox={`0 0 ${sizeState.width} ${sizeState.height}`}
        >
          <rect
            x="5"
            y="5"
            width={Math.max(sizeState.width - 10, 0)}
            height={Math.max(sizeState.height - 10, 0)}
            strokeWidth="10"
            stroke={`url(#${gradientID})`}
            rx="80"
            fill="none"
          />
          <defs>{gradientElement}</defs>
        </svg>
      ) : null}
    </span>
  );
};

export default BorderGradient;
